// Bandeja del supervisor comercial (ronda 1): las solicitudes de cambio de ficha PENDIENTES, con el
// valor actual y el solicitado. Aprobar aplica el cambio en el backend (y lo audita); rechazar exige
// una observación. Nada se aplica desde la interfaz.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { useListasPrecio, useSolicitudesCambio, useVendedores } from '../api/consultas';
import type { ClienteEncontrado, SolicitudCambio } from '../api/contratos';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { rut } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { useNombreComuna } from '../ventas/PanelContexto';
import { NOMBRE_CAMPO, textoValor, type Nombres } from './valoresFicha';

export function useNombresFicha(empresaId: string): Nombres {
  const vendedores = useVendedores(empresaId);
  const listas = useListasPrecio(empresaId);
  const comuna = useNombreComuna();
  return {
    vendedor: (id) => {
      const v = vendedores.data?.find((x) => x.id === id);
      return v ? `${v.codigo} · ${v.nombre}` : id.slice(0, 8);
    },
    lista: (id) => {
      const l = listas.data?.find((x) => x.id === id);
      return l ? `${l.codigo} ${l.nombre}` : id.slice(0, 8);
    },
    comuna,
  };
}

export function BandejaSolicitudes({
  activa,
  alAbrirCliente,
  embebida = false,
}: {
  activa: boolean;
  alAbrirCliente: (c: Pick<ClienteEncontrado, 'id' | 'razon_social'>) => void;
  /** Dentro de Firmas, bajo las notas: sin marco de pantalla propia. */
  embebida?: boolean;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const q = useSolicitudesCambio(empresaId, { estado: 'PENDIENTE' }, activa);
  const nombres = useNombresFicha(empresaId);
  const [decision, setDecision] = useState<{
    s: SolicitudCambio;
    accion: 'aprobar' | 'rechazar';
  } | null>(null);
  return (
    <div
      className={embebida ? 'firmas-fichas' : 'pantalla consulta'}
      data-pantalla={embebida ? undefined : 'solicitudes'}
    >
      <h2>Cambios de ficha de clientes · pendientes</h2>
      {q.isError && <PanelError error={q.error} />}
      <table className="grilla compacta" aria-label="Solicitudes pendientes">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Cliente</th>
            <th>Cambio pedido (actual → solicitado)</th>
            <th>Motivo</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(q.data ?? []).map((s) => (
            <tr key={s.id} data-solicitud={s.id}>
              <td>{new Date(s.solicitado_en).toLocaleString('es-CL')}</td>
              <td>
                <button
                  type="button"
                  className="enlace"
                  onClick={() => alAbrirCliente({ id: s.entidad_id, razon_social: s.razon_social })}
                >
                  {s.razon_social}
                </button>{' '}
                <span className="mono apagado">{rut(s.rut, s.dv)}</span>
              </td>
              <td>
                <ul className="lista-simple">
                  {s.campos.map((c) => (
                    <li key={c.campo}>
                      <b>{NOMBRE_CAMPO[c.campo]}</b>: {textoValor(c.campo, c.valor_actual, nombres)}{' '}
                      → <b>{textoValor(c.campo, c.valor_solicitado, nombres)}</b>
                    </li>
                  ))}
                </ul>
              </td>
              <td>{s.motivo}</td>
              <td className="acciones">
                <button
                  type="button"
                  className="primario"
                  onClick={() => setDecision({ s, accion: 'aprobar' })}
                >
                  Aprobar
                </button>
                <button
                  type="button"
                  className="peligro"
                  onClick={() => setDecision({ s, accion: 'rechazar' })}
                >
                  Rechazar
                </button>
              </td>
            </tr>
          ))}
          {q.isSuccess && !q.data.length && (
            <tr>
              <td colSpan={5} className="grilla-vacia">
                No hay solicitudes pendientes
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {decision && (
        <DecidirSolicitud
          empresaId={empresaId}
          solicitud={decision.s}
          accion={decision.accion}
          alCerrar={() => setDecision(null)}
        />
      )}
    </div>
  );
}

function DecidirSolicitud({
  empresaId,
  solicitud,
  accion,
  alCerrar,
}: {
  empresaId: string;
  solicitud: SolicitudCambio;
  accion: 'aprobar' | 'rechazar' | 'cancelar';
  alCerrar: () => void;
}) {
  const qc = useQueryClient();
  const [observacion, setObservacion] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api.post(`/entidades/solicitudes-cambio/${solicitud.id}/${accion}`, {
        version: solicitud.version,
        ...(observacion.trim() ? { observacion: observacion.trim() } : {}),
      }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'solicitudes-cambio'] }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'cliente', solicitud.entidad_id] }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'contexto-comercial'] }),
      ]);
      alCerrar();
    },
  });
  // Aprobar y rechazar son decisiones sensibles: quedan con su observación en la auditoría.
  const exige = accion !== 'cancelar';
  const titulo =
    accion === 'aprobar'
      ? 'Aprobar el cambio'
      : accion === 'rechazar'
        ? 'Rechazar el cambio'
        : 'Cancelar mi solicitud';
  return (
    <Modal
      titulo={`${titulo} · ${solicitud.razon_social}`}
      ancho="angosto"
      alCerrar={() => !m.isPending && alCerrar()}
      pie={
        <>
          <button type="button" onClick={alCerrar} disabled={m.isPending}>
            Volver
          </button>
          <button
            type="button"
            className={accion === 'aprobar' ? 'primario' : 'peligro'}
            disabled={m.isPending || (exige && observacion.trim().length < 3)}
            onClick={() => m.mutate()}
          >
            {m.isPending ? 'Procesando…' : titulo}
          </button>
        </>
      }
    >
      {accion === 'aprobar' && (
        <p>Al aprobar, el backend aplica el cambio en la ficha y lo deja en la auditoría.</p>
      )}
      <label className="campo">
        <span className="etiqueta">Observación{exige ? ' (obligatoria)' : ' (opcional)'}</span>
        <input
          aria-label="Observación"
          data-autofoco
          value={observacion}
          onChange={(e) => setObservacion(e.target.value)}
        />
      </label>
      {m.isError && <PanelError error={m.error} />}
    </Modal>
  );
}

export { DecidirSolicitud };
